// Small line-based diff helpers for the Text Compare tool. The `diff` package
// is only a transitive dependency (via react-diff-viewer-continued), so we keep
// a self-contained Myers diff here instead of importing it directly.

export const splitLines = (text) => (text === "" ? [] : String(text).split(/\r?\n/));

// Apply the "ignore" options to a text. The result is what both the diff
// viewer and the stats operate on.
export const normalizeText = (text, { ignoreCase, ignoreWhitespace, ignoreBlankLines }) => {
  let lines = splitLines(text);
  if (ignoreWhitespace) lines = lines.map((l) => l.trim());
  if (ignoreBlankLines) lines = lines.filter((l) => l.trim() !== "");
  let out = lines.join("\n");
  if (ignoreCase) out = out.toLowerCase();
  return out;
};

// Cap on the number of Int32 cells kept in the Myers trace (~80 MB). Inputs
// whose edit distance would exceed it fall back to "remove all, add all" for
// the differing middle section, which is still a valid (if not minimal) diff.
const MAX_TRACE_CELLS = 2e7;

// Myers O(ND) diff over two arrays of lines. Returns an ordered list of
// { type: "equal" | "add" | "remove", line } operations.
export const diffLines = (a, b) => {
  // Strip the common prefix/suffix first; it keeps the edit graph small for
  // the usual "mostly identical" inputs.
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }

  const ops = [];
  for (let i = 0; i < start; i++) ops.push({ type: "equal", line: a[i] });

  const x0 = a.slice(start, endA);
  const y0 = b.slice(start, endB);
  const n = x0.length;
  const m = y0.length;
  const max = n + m;
  const offset = max;
  const v = new Int32Array(2 * max + 2);
  const trace = [];

  let found = n === 0 && m === 0;
  for (let d = 0; d <= max && !found; d++) {
    if ((d + 1) * v.length > MAX_TRACE_CELLS) break;
    trace.push(v.slice());
    for (let k = -d; k <= d; k += 2) {
      let x;
      if (k === -d || (k !== d && v[offset + k - 1] < v[offset + k + 1])) {
        x = v[offset + k + 1];
      } else {
        x = v[offset + k - 1] + 1;
      }
      let y = x - k;
      while (x < n && y < m && x0[x] === y0[y]) {
        x++;
        y++;
      }
      v[offset + k] = x;
      if (x >= n && y >= m) {
        found = true;
        break;
      }
    }
  }

  const middle = [];
  if (!found) {
    for (const line of x0) middle.push({ type: "remove", line });
    for (const line of y0) middle.push({ type: "add", line });
    ops.push(...middle);
    for (let i = endA; i < a.length; i++) ops.push({ type: "equal", line: a[i] });
    return ops;
  }

  // Backtrack through the saved V arrays to recover the edit script.
  let x = n;
  let y = m;
  for (let d = trace.length - 1; d >= 0 && (x > 0 || y > 0); d--) {
    const vd = trace[d];
    const k = x - y;
    let prevK;
    if (k === -d || (k !== d && vd[offset + k - 1] < vd[offset + k + 1])) {
      prevK = k + 1;
    } else {
      prevK = k - 1;
    }
    const prevX = vd[offset + prevK];
    const prevY = prevX - prevK;
    while (x > prevX && y > prevY) {
      middle.push({ type: "equal", line: x0[x - 1] });
      x--;
      y--;
    }
    if (d > 0) {
      if (x === prevX) middle.push({ type: "add", line: y0[y - 1] });
      else middle.push({ type: "remove", line: x0[x - 1] });
    }
    x = prevX;
    y = prevY;
  }
  middle.reverse();
  ops.push(...middle);

  for (let i = endA; i < a.length; i++) ops.push({ type: "equal", line: a[i] });
  return ops;
};

export const diffStats = (ops) => {
  const stats = { added: 0, removed: 0, unchanged: 0 };
  for (const op of ops) {
    if (op.type === "add") stats.added++;
    else if (op.type === "remove") stats.removed++;
    else stats.unchanged++;
  }
  return stats;
};

// Standard unified diff text (as produced by `diff -u`), with `context` lines
// of context around each hunk. Returns "" when there are no changes.
export const unifiedDiff = (ops, { oldName = "original.txt", newName = "modified.txt", context = 3 } = {}) => {
  if (!ops.some((op) => op.type !== "equal")) return "";

  // Annotate each op with its 1-based line numbers in the old/new files.
  let oldLine = 1;
  let newLine = 1;
  const rows = ops.map((op) => {
    const row = { ...op, oldLine, newLine };
    if (op.type !== "add") oldLine++;
    if (op.type !== "remove") newLine++;
    return row;
  });

  // Group changes into hunks, merging those whose context would overlap.
  const hunks = [];
  let current = null;
  rows.forEach((row, i) => {
    if (row.type === "equal") return;
    const from = Math.max(0, i - context);
    const to = Math.min(rows.length - 1, i + context);
    if (current && from <= current.to + 1) {
      current.to = Math.max(current.to, to);
    } else {
      current = { from, to };
      hunks.push(current);
    }
  });

  const out = [`--- ${oldName}`, `+++ ${newName}`];
  for (const { from, to } of hunks) {
    const slice = rows.slice(from, to + 1);
    const oldCount = slice.filter((r) => r.type !== "add").length;
    const newCount = slice.filter((r) => r.type !== "remove").length;
    // Per the unified format, an empty range starts one line before.
    const oldStart = oldCount === 0 ? slice[0].oldLine - 1 : slice[0].oldLine;
    const newStart = newCount === 0 ? slice[0].newLine - 1 : slice[0].newLine;
    out.push(`@@ -${oldStart},${oldCount} +${newStart},${newCount} @@`);
    for (const r of slice) {
      const prefix = r.type === "add" ? "+" : r.type === "remove" ? "-" : " ";
      out.push(prefix + r.line);
    }
  }
  return out.join("\n") + "\n";
};
